import classNames from 'classnames'
import Link from 'next/link'
import { Fragment, useEffect, useState } from 'react'
import { v4 } from 'uuid'

import crosswordIcon from '@/assets/crossword.svg'
import salt_48 from '@/assets/salt_48.png'
import spellifyIcon from '@/assets/spellify_icon.svg'
import Icon from '@/components/Icon/Icon'
import { COMBO_NAV_ENABLED, NavbarConfig } from '@/components/Layout/Navbar/Navbar'
import Mana from '@/components/Mana/Mana'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { useActions, useAppSelector } from '@/store/hooks'
import { signOutAction } from '@/store/user/actions/signOut'

import { SALT_NAV_ENABLED } from '../Navbar/Navbar'

import styles from './MobileNav.module.scss'

type Props = {
  expanded: boolean
  handleRandom: () => void
  navBarConfig: NavbarConfig[]
  setShowSettings: (show: boolean) => void
  trackNavInternal: (destination: string, label?: string) => void
}

const MobileNav = ({ expanded, handleRandom, navBarConfig, setShowSettings, trackNavInternal }: Props) => {
  const [subMenuIndex, setSubMenuIndex] = useState<number | null>(null)
  const [finalMenuIndex, setFinalMenuIndex] = useState<number | null>(null)
  const displayName = useAppSelector((state) => state.user.displayName)
  const [signOut] = useActions(signOutAction)

  const extraSubmenus: NavbarConfig[] = [
    {
      background: 'https://card-images.edhrec.com/art_crop/front/b/5/b5ddad46-5e2e-43c9-8c91-7aca6ca23562.jpg',
      id: 'extras',
      submenu: [
        { icon: 'gear', onClick: () => setShowSettings(true), value: 'Settings' },
        { icon: 'random', onClick: () => handleRandom(), value: 'Random Commander' },
        { href: '/recs', icon: 'chartSimple', value: 'Recs' },
        { href: '/faq', icon: 'question', value: 'FAQ' },
        { href: '/newsletter', icon: 'newspaper', value: 'Newsletter' },
        {
          href: 'https://www.patreon.com/edhrec?utm_source=edhrec&utm_medium=mobile_navbar',
          icon: 'patreon',
          value: 'Patreon',
        },
        { href: '/games/spellify', invertedSvgFile: spellifyIcon.src, value: 'Spellify' },
        { href: '/games/crossword', invertedSvgFile: crosswordIcon.src, value: 'Crossword' },
        { href: '/games/quiz', icon: 'squarePollVertical', value: 'Personality Quizzes' },
        { href: '/contact-us', icon: 'envelope', value: 'Contact Us' },
      ],
      title: 'Extras',
    },
    {
      background: 'https://card-images.edhrec.com/art_crop/front/8/0/806d4e61-004f-4e52-b2e8-ba11b922cc7d.jpg',
      id: 'user',
      submenu: displayName
        ? [
            { href: '/account', value: 'My Account' },
            { onClick: signOut, value: 'Sign Out' },
          ]
        : [
            { href: '/signin', value: 'Sign In' },
            { href: '/signup', value: 'Sign Up' },
          ],
      title: 'User',
    },
  ]
  if (COMBO_NAV_ENABLED) extraSubmenus[0].submenu?.push({ href: '/combo-vote', icon: 'infinity', value: 'Combo Vote' })

  const handleList1Click = (index: number) => {
    if (subMenuIndex === index) {
      setSubMenuIndex(null)
    } else {
      setSubMenuIndex(index)
    }
  }

  const handleList2Click = (index: number) => {
    if (finalMenuIndex === index) {
      setFinalMenuIndex(null)
    } else {
      setFinalMenuIndex(index)
    }
  }

  useEffect(() => {
    setSubMenuIndex(null)
    if (expanded) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = 'auto'
  }, [expanded])

  useEffect(() => {
    setFinalMenuIndex(null)
  }, [subMenuIndex])

  return (
    <nav className={classNames(styles.mobileNav, !expanded && styles.hidden)}>
      <ul className={styles.list1}>
        {navBarConfig.map((item, index) => (
          <li
            className={classNames(
              subMenuIndex === index && styles.selectedList1,
              subMenuIndex !== null && subMenuIndex !== index && styles.unselectedList1,
            )}
            key={item.id}
          >
            {item.href && (
              <Link href={item.href} onClick={() => trackNavInternal(item.href || '')}>
                {item.icon && <Icon icon={item.icon} />}
                &nbsp;
                {item.title}
              </Link>
            )}
            {!item.href && item.submenu && (
              <a className='d-flex justify-content-between align-items-center' onClick={() => handleList1Click(index)}>
                <div>
                  {item.icon && <Icon icon={item.icon} />}
                  &nbsp;
                  {item.title}
                </div>
                <Icon icon='caretRight' />
              </a>
            )}
          </li>
        ))}
        <li
          className={classNames(
            'd-flex flex-column',
            subMenuIndex === navBarConfig.length && styles.selectedList1,
            subMenuIndex !== null && subMenuIndex !== navBarConfig.length && styles.unselectedList1,
            styles.noBottomBorder,
          )}
        >
          <a
            className='d-flex justify-content-between align-items-center'
            onClick={() => handleList1Click(navBarConfig.length)}
          >
            <span>
              <Icon icon='link' /> More
            </span>
            <Icon icon='caretRight' />
          </a>
        </li>
        <li className={styles.noBottomBorder} onClick={() => setSubMenuIndex(null)}>
          <div className={classNames(styles.extrasLinkContainer, subMenuIndex !== null && styles.subHidden)}>
            <a className={styles.extraIconButton} onClick={() => setShowSettings(true)}>
              <Icon icon='gear' />
              <span>Settings</span>
            </a>
            <a className={styles.extraIconButton} onClick={handleRandom}>
              <Icon icon='random' />
              <span>Random</span>
            </a>
            <Link className={styles.extraIconButton} href='/recs'>
              <Icon icon='chartSimple' />
              <span>Recs</span>
            </Link>
            <Link className={styles.extraIconButton} href='/faq'>
              <Icon icon='question' />
              <span>FAQ</span>
            </Link>
            <OutboundLink className={styles.extraIconButton} url='https://www.patreon.com/edhrec' utm_medium='navbar'>
              <Icon icon='patreon' />
              <span>Patreon</span>
            </OutboundLink>
            <OutboundLink
              className={styles.extraIconButton}
              url='https://shop.spacecowmedia.com/collections/edhrec'
              utm_medium='navbar'
            >
              <Icon icon='shop' />
              <span>Merch</span>
            </OutboundLink>
            <OutboundLink
              className={styles.extraIconButton}
              url='https://edhrec.com/games/crossword'
              utm_medium='navbar'
            >
              <img alt='Crossword' className={styles.spellifyIcon} src={crosswordIcon.src} />
              <span>Crossword</span>
            </OutboundLink>
            <OutboundLink
              className={styles.extraIconButton}
              url='https://edhrec.com/games/spellify'
              utm_medium='navbar'
            >
              <img alt='Spellify' className={styles.spellifyIcon} src={spellifyIcon.src} />
              <span>Spellify</span>
            </OutboundLink>
            <OutboundLink className={styles.extraIconButton} url='https://edhrec.com/games/quiz' utm_medium='navbar'>
              <Icon icon='squarePollVertical' />
              <span>Quizzes</span>
            </OutboundLink>
            {SALT_NAV_ENABLED && (
              <Link className={classNames(styles.extraIconButton, styles.salt)} href='/salt'>
                <img alt='Salt Score' src={salt_48.src} />
                <span>Salt Vote</span>
              </Link>
            )}
            {COMBO_NAV_ENABLED && (
              <Link className={classNames(styles.extraIconButton, styles.salt)} href='/combo-vote'>
                <Icon icon='infinity' />
                <span>Combo Vote</span>
              </Link>
            )}
          </div>
        </li>
        <li className='flex-grow-1' onClick={() => setSubMenuIndex(null)} />
        <li
          className={classNames(
            'd-flex',
            subMenuIndex === navBarConfig.length + 1 && styles.selectedList1,
            subMenuIndex !== null && subMenuIndex !== navBarConfig.length + 1 && styles.unselectedList1,
          )}
        >
          <a className={styles.userIconContainer} onClick={() => handleList1Click(navBarConfig.length + 1)}>
            <Icon icon='circleUser' />
          </a>
          <div className='d-flex flex-grow-1'>
            {!displayName && (
              <>
                <Link
                  className={classNames(styles.authButton, styles.leftClip, 'bg-secondary')}
                  href='/signin'
                  onClick={() => trackNavInternal('/signin')}
                >
                  Sign In
                </Link>
                <Link
                  className={classNames(styles.authButton, styles.rightClip, 'bg-primary')}
                  href='/signup'
                  onClick={() => trackNavInternal('/signup')}
                >
                  Sign Up
                </Link>
              </>
            )}

            {displayName && (
              <div
                className={classNames(
                  styles.rightClip,
                  'bg-secondary w-100 d-flex flex-column justify-content-center align-items-center',
                )}
              >
                <div>
                  <b>{displayName}</b>
                </div>
                <div className='d-flex gap-2'>
                  <Link
                    className={classNames(styles.authLink, 'p-0')}
                    href='/account'
                    onClick={() => trackNavInternal('/account')}
                  >
                    Account
                  </Link>
                  |
                  <a className={classNames(styles.authLink, 'p-0')} onClick={signOut}>
                    Sign Out
                  </a>
                </div>
              </div>
            )}
          </div>
        </li>
      </ul>
      <div className={classNames(styles.list2Container, subMenuIndex === null && styles.list2ContainerHidden)}>
        {[...navBarConfig, ...extraSubmenus].map(
          (item, index) =>
            item.submenu && (
              <Fragment key={item.id}>
                <ul className={classNames(styles.list2, subMenuIndex !== index && styles.subHidden)}>
                  {item.submenu.map((subItem, finalIndex) => (
                    <Fragment key={v4()}>
                      <li className={subItem.className}>
                        {subItem.href && (
                          <Link href={subItem.href} onClick={() => trackNavInternal(subItem.href || '')}>
                            <div className='d-flex align-items-center gap-2'>
                              {subItem.icon_svg_uri && (
                                <img
                                  alt={subItem.alt || (subItem.value as string)}
                                  className={styles.setIcon}
                                  src={subItem.icon_svg_uri}
                                />
                              )}
                              {subItem.icon && <Icon icon={subItem.icon} />}
                              {subItem.colors && (
                                <span className='me-1 text-nowrap'>
                                  <Mana colors={subItem.colors} />
                                </span>
                              )}
                              {subItem.invertedSvgFile && (
                                <>
                                  <img
                                    alt={subItem.alt || (subItem.value as string)}
                                    className={styles.invertedSvg}
                                    src={subItem.invertedSvgFile}
                                  />
                                  &nbsp;
                                </>
                              )}
                              {subItem.value}
                            </div>
                          </Link>
                        )}
                        {!subItem.href && subItem.submenu && (
                          <a
                            className='d-flex justify-content-between align-items-center'
                            onClick={() => handleList2Click(finalIndex)}
                          >
                            <div>{subItem.title}</div>
                            <Icon icon='caretDown' />
                          </a>
                        )}
                        {!subItem.href && !subItem.submenu && subItem.onClick && (
                          <a className='d-flex justify-content-between align-items-center' onClick={subItem.onClick}>
                            <span>
                              {subItem.icon && (
                                <>
                                  <Icon icon={subItem.icon} />
                                  &nbsp;
                                </>
                              )}
                              {subItem.invertedSvgFile && (
                                <>
                                  <img
                                    alt={subItem.alt || (subItem.value as string)}
                                    className={styles.invertedSvg}
                                    src={subItem.invertedSvgFile}
                                  />
                                  &nbsp;
                                </>
                              )}
                              {subItem.value}
                            </span>
                          </a>
                        )}
                      </li>
                      {subItem.submenu && (
                        <li
                          className={classNames(
                            styles.list3Container,
                            !(subMenuIndex === index && finalIndex === finalMenuIndex) && styles.list3ContainerHidden,
                          )}
                          style={{
                            height:
                              subMenuIndex === index && finalIndex === finalMenuIndex ? 49 * subItem.submenu.length : 0,
                          }}
                        >
                          <ul className={classNames(styles.list3)}>
                            {subItem.submenu.map((subSubItem) => (
                              <li key={v4()}>
                                {subSubItem.href ? (
                                  <Link href={subSubItem.href} onClick={() => trackNavInternal(subItem.href || '')}>
                                    {(subSubItem.color || subSubItem.colors) && (
                                      <>
                                        <Mana color={subSubItem.color} colors={subSubItem.colors} />
                                        &nbsp;
                                      </>
                                    )}
                                    {subSubItem.value}
                                  </Link>
                                ) : (
                                  subSubItem.value
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </Fragment>
                  ))}
                </ul>
                {item.background && (
                  <div
                    className={classNames(styles.subBackground, subMenuIndex !== index && styles.subHidden)}
                    key={item.id}
                    style={{ backgroundImage: `url(${item.background})` }}
                  />
                )}
              </Fragment>
            ),
        )}
      </div>
    </nav>
  )
}

export default MobileNav
